import { type RefObject, useEffect, useRef, useState } from "react";
import { fetchShopItems } from "../../Api";
import OfferGroupView from "../../components/OfferGroupView";
import PlayerSearchError from "../../components/PlayerSearchError";
import ShopItemView, { type CardVariant } from "../../components/ShopItemView/ShopItemView";
import Spinner from "../../components/Spinner";
import VipItemView from "../../components/VipItemView";
import type { OfferItem, ShopItem, VipStatus, VipStatusItem } from "../../types";
import styles from "./chooseProduct.module.css";

type Props = {
  userId: number;
  onShopItemClick: (shopItem: ShopItem, vipStatus: VipStatus | null) => void;
  shouldAutoScrollToPreloadedPlayer: boolean;
  onPreloadedPlayerReady: () => void;
};

type TabId = "offers" | "chips" | "tickets" | "vip";

type OfferEntry = { kind: "single"; item: OfferItem } | { kind: "group"; groupId: string; items: OfferItem[] };

function groupOffers(offers: OfferItem[]): OfferEntry[] {
  const entries: OfferEntry[] = [];
  const groups = new Map<string, OfferItem[]>();

  for (const offer of offers) {
    if (!offer.groupId) {
      entries.push({ kind: "single", item: offer });
      continue;
    }

    const groupItems = groups.get(offer.groupId);
    if (groupItems) {
      groupItems.push(offer);
      continue;
    }

    const items = [offer];
    groups.set(offer.groupId, items);
    entries.push({ kind: "group", groupId: offer.groupId, items });
  }

  return entries.map((entry): OfferEntry => {
    const [onlyItem] = entry.kind === "group" && entry.items.length === 1 ? entry.items : [];
    return onlyItem ? { kind: "single", item: onlyItem } : entry;
  });
}

function getCardVariant(product: ShopItem, group: string, isOffer: boolean): CardVariant {
  if (isOffer) {
    return group === "Tickets" ? "violetOffer" : "redOffer";
  }
  if (product.labelType === "bestPrice") return "purple";
  if (product.labelType === "everyoneTakes") return "orange";
  if (product.labelType) return "orange";
  return "common";
}

function getInitialTab(
  offers: OfferItem[],
  chips: ShopItem[],
  tickets: ShopItem[],
  vipStatusItems: VipStatusItem[],
): TabId {
  if (offers.length > 0) return "offers";
  if (chips.length > 0) return "chips";
  if (tickets.length > 0) return "tickets";
  if (vipStatusItems.length > 0) return "vip";
  return "chips";
}

export default function ChooseProduct({
  userId,
  onShopItemClick,
  shouldAutoScrollToPreloadedPlayer,
  onPreloadedPlayerReady,
}: Props) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chipsProducts, setChipsProducts] = useState<ShopItem[]>([]);
  const [ticketsProducts, setTicketsProducts] = useState<ShopItem[]>([]);
  const [offerProducts, setOfferProducts] = useState<OfferItem[]>([]);
  const [vipItems, setVipItems] = useState<VipStatusItem[]>([]);
  const [vipStatus, setVipStatus] = useState<VipStatus | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>("chips");

  const offersTabRef = useRef<HTMLButtonElement>(null);
  const chipsTabRef = useRef<HTMLButtonElement>(null);
  const ticketsTabRef = useRef<HTMLButtonElement>(null);
  const vipTabRef = useRef<HTMLButtonElement>(null);
  const offersRef = useRef<HTMLDivElement>(null);
  const chipsRef = useRef<HTMLDivElement>(null);
  const ticketsRef = useRef<HTMLDivElement>(null);
  const vipRef = useRef<HTMLDivElement>(null);
  const hasAutoScrolledRef = useRef(false);

  const scrollActiveTabIntoView = (tabId: TabId) => {
    const refMap: Record<TabId, RefObject<HTMLButtonElement | null>> = {
      offers: offersTabRef,
      chips: chipsTabRef,
      tickets: ticketsTabRef,
      vip: vipTabRef,
    };

    refMap[tabId].current?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  };

  const scrollToTab = (tabId: TabId) => {
    const refMap: Record<TabId, RefObject<HTMLDivElement | null>> = {
      offers: offersRef,
      chips: chipsRef,
      tickets: ticketsRef,
      vip: vipRef,
    };

    refMap[tabId].current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    let isMounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      const response = await fetchShopItems(userId);
      if (!isMounted) return;
      setLoading(false);
      if (!response.success) {
        setError(response.reason);
        return;
      }
      const { chips, tickets, offers, vipStatusItems, vipStatus } = response.data;
      setChipsProducts(chips);
      setTicketsProducts(tickets);
      setOfferProducts(offers);
      setVipItems(vipStatusItems);
      setVipStatus(vipStatus);
      setActiveTab(getInitialTab(offers, chips, tickets, vipStatusItems));
    })();
    return () => {
      isMounted = false;
    };
  }, [userId]);

  useEffect(() => {
    if (!shouldAutoScrollToPreloadedPlayer || loading || error || hasAutoScrolledRef.current) return;

    hasAutoScrolledRef.current = true;
    requestAnimationFrame(() => {
      scrollActiveTabIntoView(activeTab);
      onPreloadedPlayerReady();
    });
  }, [activeTab, error, loading, onPreloadedPlayerReady, shouldAutoScrollToPreloadedPlayer]);

  const handleTabClick = (tabId: TabId) => {
    setActiveTab(tabId);
    scrollActiveTabIntoView(tabId);
    scrollToTab(tabId);
  };

  const hasOffers = offerProducts.length > 0;
  const hasVip = vipItems.length > 0;
  const hasChips = chipsProducts.length > 0;
  const hasTickets = ticketsProducts.length > 0;

  return (
    <div className={styles.container}>
      {loading && <Spinner />}

      {error && (
        <div className={styles.errorWrap}>
          <PlayerSearchError message={error} />
        </div>
      )}

      {!loading && !error && (
        <>
          {/* Tab Bar */}
          <div className={styles.tabBar}>
            {hasOffers && (
              <button
                ref={offersTabRef}
                className={`${styles.tab} ${activeTab === "offers" ? styles.tabSelected : ""}`}
                onClick={() => handleTabClick("offers")}
              >
                <span>Акция</span>
              </button>
            )}
            {hasChips && (
              <button
                ref={chipsTabRef}
                className={`${styles.tab} ${activeTab === "chips" ? styles.tabSelected : ""}`}
                onClick={() => handleTabClick("chips")}
              >
                <img width="18" height="18" src="chips_icon_pill.svg" alt="" />
                <span>Фишки</span>
              </button>
            )}
            {hasTickets && (
              <button
                ref={ticketsTabRef}
                className={`${styles.tab} ${activeTab === "tickets" ? styles.tabSelected : ""}`}
                onClick={() => handleTabClick("tickets")}
              >
                <img width="18" height="18" src="tickets_icon_pill.svg" alt="" />
                <span>Билетики</span>
              </button>
            )}
            {hasVip && (
              <button
                ref={vipTabRef}
                className={`${styles.tab} ${activeTab === "vip" ? styles.tabSelected : ""}`}
                onClick={() => handleTabClick("vip")}
              >
                <img width="18" height="18" src="vip_icon_pill.svg" alt="" />
                <span>VIP</span>
              </button>
            )}
          </div>

          <div className={styles.content}>
            {/* Offers Section */}
            {hasOffers && (
              <div className={styles.section} ref={offersRef}>
                <div className={styles.sectionDivider}>
                  <div className={styles.dividerLine} />
                  <div className={styles.dividerText}>Акция</div>
                  <div className={styles.dividerLine} />
                </div>
                {groupOffers(offerProducts).map((entry) =>
                  entry.kind === "group" ? (
                    <OfferGroupView
                      key={entry.groupId}
                      items={entry.items}
                      getVariant={(item) => getCardVariant(item, item.group, true)}
                      vipStatus={vipStatus}
                      onChooseClick={(item) => onShopItemClick(item, vipStatus)}
                    />
                  ) : (
                    <ShopItemView
                      key={`${entry.item.id}:${entry.item.claimParameter}`}
                      product={entry.item}
                      variant={getCardVariant(entry.item, entry.item.group, true)}
                      expirationDate={entry.item.expirationDate}
                      vipStatus={vipStatus}
                      onChooseClick={() => onShopItemClick(entry.item, vipStatus)}
                    />
                  ),
                )}
              </div>
            )}

            {/* Chips Section */}
            {hasChips && (
              <div className={styles.section} ref={chipsRef}>
                <div className={styles.sectionDivider}>
                  <div className={styles.dividerLine} />
                  <div className={styles.dividerText}>Фишки</div>
                  <div className={styles.dividerLine} />
                </div>
                {chipsProducts.map((product) => (
                  <ShopItemView
                    key={product.id}
                    product={product}
                    variant={getCardVariant(product, product.group, false)}
                    vipStatus={vipStatus}
                    onChooseClick={() => onShopItemClick(product, vipStatus)}
                  />
                ))}
              </div>
            )}

            {/* Tickets Section */}
            {hasTickets && (
              <div className={styles.section} ref={ticketsRef}>
                <div className={styles.sectionDivider}>
                  <div className={styles.dividerLine} />
                  <div className={styles.dividerText}>Билетики</div>
                  <div className={styles.dividerLine} />
                </div>
                {ticketsProducts.map((product) => (
                  <ShopItemView
                    key={product.id}
                    product={product}
                    variant={getCardVariant(product, product.group, false)}
                    vipStatus={vipStatus}
                    onChooseClick={() => onShopItemClick(product, vipStatus)}
                  />
                ))}
              </div>
            )}

            {/* VIP Section */}
            {hasVip && (
              <div className={styles.section} ref={vipRef}>
                <div className={styles.sectionDivider}>
                  <div className={styles.dividerLine} />
                  <div className={styles.dividerText}>VIP-подписка</div>
                  <div className={styles.dividerLine} />
                </div>
                {vipItems.map((product) => (
                  <VipItemView key={product.id} product={product} onChooseClick={() => onShopItemClick(product)} />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
